import React, { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuthHook } from '../../hooks/useAuth.hook';
import { useAuthorization, type Permission } from '../../hooks/useAuthorization.hook';
import { Routes } from '../../types/enums/Routes.enum';
import { AccessDenied } from '../AccessDenied';
import { Loading } from '../Loading';
import { storage } from '../../utils/Const.utils';
import { StorageKeys } from '../../types/enums/Storage.enum';

/**
 * Props para el componente ProtectedRoute
 */
interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: Permission[];
  requireAnyPermission?: boolean;
  fallbackComponent?: ReactNode;
  redirectTo?: string;
  showAccessDenied?: boolean;
  loadingVariant?: 'circular' | 'linear' | 'pulsing' | 'dots';
  accessDeniedVariant?: 'default' | 'minimal' | 'detailed';
  showContactInfo?: boolean;
}

/**
 * Componente que protege rutas requiriendo autenticación
 * y opcionalmente permisos específicos
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requireAnyPermission = false,
  fallbackComponent,
  redirectTo = Routes.DEFAULT,
  showAccessDenied = false,
  loadingVariant = 'circular',
  accessDeniedVariant = 'default',
  showContactInfo = false,
}) => {
  const { isAuthenticated, isLoading, requireAuth } = useAuthHook();
  const location = useLocation();
  
  /* Usar el hook de autorización para manejar permisos */
  const authorization = useAuthorization(requiredPermissions, {
    redirectTo,
    requireAll: !requireAnyPermission,
  });

  /* Al recargar, AuthProvider inicializa la sesión (INIT_AUTH) después de este efecto; redirigir antes
     enviaría a /login a un usuario autenticado y se perdería el location.state de la página */
  const sesionInicializadaRef = useRef(!isLoading);

  useEffect(() => {
    if (!isLoading) sesionInicializadaRef.current = true;
    if (!sesionInicializadaRef.current) return;

    /* Verificar autenticación automáticamente */
    requireAuth();
  }, [requireAuth, isLoading]);

  /* Mostrar loading si está cargando la autenticación */
  if (isLoading || authorization.isLoading) {
    return (
      <Loading
        variant={loadingVariant}
        message="Verificando acceso..."
        fullscreen
        showIcon
      />
    );
  }

  /* Redirigir al login si no está autenticado */
  if (!isAuthenticated) {
    return (
      <Navigate
        to={Routes.LOGIN}
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  /* Verificar si el usuario completó la selección de rol/perfil */
  const hasCompletedRolSelection = storage.get(StorageKeys.PERFIL);
  const currentPath = location.pathname;
  
  /* Si no ha completado la selección de perfil y no está en la ruta de selección, redirigir */
  if (!hasCompletedRolSelection && currentPath !== Routes.POST_LOGIN_ROL) {
    return (
      <Navigate
        to={Routes.POST_LOGIN_ROL}
        state={{ from: currentPath + location.search }}
        replace
      />
    );
  }

  /* Verificar autorización si se especificaron permisos */
  if (requiredPermissions.length > 0 && !authorization.isAuthorized) {
    /* Si hay un componente de fallback personalizado, usarlo */
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }

    /* Si se especifica mostrar página de acceso denegado */
    if (showAccessDenied) {
      return (
        <AccessDenied
          variant={accessDeniedVariant}
          showContactInfo={showContactInfo}
          requiredPermissions={requiredPermissions}
          customActions={
            accessDeniedVariant === 'minimal' ? (
              <button
                onClick={() => window.history.back()}
                className="bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-md transition-colors"
              >
                Volver
              </button>
            ) : undefined
          }
        />
      );
    }

    /* Por defecto: redirigir con estado de error */
    return (
      <Navigate
        to={redirectTo}
        state={{ 
          message: 'No tienes permisos para acceder a la sección solicitada.',
          severity: 'warning',
          from: location.pathname,
          missingPermissions: authorization.missingPermissions
        }}
        replace
      />
    );
  }

  /* Si todo está correcto, mostrar el contenido protegido */
  return <>{children}</>;
};

/**
 * Props para ProtectedLayout
 */
interface ProtectedLayoutProps {
  children: ReactNode;
  layout: React.ComponentType<{ children: ReactNode }>;
  requiredPermissions?: Permission[];
  requireAnyPermission?: boolean;
  redirectTo?: string;
  showAccessDenied?: boolean;
  loadingVariant?: 'circular' | 'linear' | 'pulsing' | 'dots';
  accessDeniedVariant?: 'default' | 'minimal' | 'detailed';
}

/**
 * Componente que protege layouts completos
 */
export const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({
  children,
  layout: Layout,
  requiredPermissions,
  requireAnyPermission,
  redirectTo,
  showAccessDenied,
  loadingVariant = 'pulsing',
  accessDeniedVariant = 'detailed',
}) => {
  return (
    <ProtectedRoute
      requiredPermissions={requiredPermissions}
      requireAnyPermission={requireAnyPermission}
      redirectTo={redirectTo}
      showAccessDenied={showAccessDenied}
      loadingVariant={loadingVariant}
      accessDeniedVariant={accessDeniedVariant}
    >
      <Layout>
        {children}
      </Layout>
    </ProtectedRoute>
  );
};

/**
 * HOC para proteger componentes individuales
 */
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>,
  requiredPermissions?: Permission[],
  requireAnyPermission = false,
  options?: {
    redirectTo?: string;
    showAccessDenied?: boolean;
    loadingVariant?: 'circular' | 'linear' | 'pulsing' | 'dots';
    accessDeniedVariant?: 'default' | 'minimal' | 'detailed';
  }
) => {
  const WrappedComponent = (props: P) => {
    return (
      <ProtectedRoute
        requiredPermissions={requiredPermissions}
        requireAnyPermission={requireAnyPermission}
        redirectTo={options?.redirectTo}
        showAccessDenied={options?.showAccessDenied}
        loadingVariant={options?.loadingVariant}
        accessDeniedVariant={options?.accessDeniedVariant}
      >
        <Component {...props} />
      </ProtectedRoute>
    );
  };

  WrappedComponent.displayName = `withAuth(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default ProtectedRoute;
